import { getUrl } from "aws-amplify/storage";

import { downloadFile, getFileBlob } from "./download";
import {
  handleStorageError,
  isEmptyStorageKey,
  STORAGE_BUCKET,
  STORAGE_ENDPOINT
} from "./shared";
import { CLOUD_FRONT_DOMAIN_NAME } from "@app/bootstrap/init";
import { isKamiliaSandbox, isMikeSandbox } from "@utils/sandboxEnv";

const BLOB_URL_REVOKE_DELAY_MS = 150;

const revokeBlobUrlAfterDelay = (blobUrl: string): void => {
  setTimeout(() => URL.revokeObjectURL(blobUrl), BLOB_URL_REVOKE_DELAY_MS);
};

export const getImageUrl = async (s3Key: string): Promise<string> => {
  try {
    if (isEmptyStorageKey(s3Key)) return "";
    const { url } = await getUrl({
      options: { bucket: STORAGE_BUCKET, ...STORAGE_ENDPOINT },
      path: s3Key
    });
    return url.toString();
  } catch (error) {
    handleStorageError(error);
    return "";
  }
};

export const openFileInNewTab = async (s3Key: string): Promise<void> => {
  if (isEmptyStorageKey(s3Key)) return;

  try {
    const blob = await getFileBlob(s3Key);

    const { getMimeType } = await import("catchment-common/utils/file");
    const mimeType = getMimeType(s3Key) || blob.type;
    const blobWithType = new Blob([blob], { type: mimeType });

    const blobUrl = URL.createObjectURL(blobWithType);
    const newWindow = window.open(blobUrl, "_blank");

    if (newWindow) {
      revokeBlobUrlAfterDelay(blobUrl);
    }
  } catch (error) {
    handleStorageError(error);
  }
};

/** Download file (Excel/CSV) or open in new tab (e.g. PDF). No-op if uri is empty. */
export const viewOrDownloadFile = async (uri: string): Promise<void> => {
  if (isEmptyStorageKey(uri)) return;

  const { getFileExtension, isDownloadableFile } =
    await import("catchment-common/utils/file");
  const { getFileName } = await import("catchment-common/utils/string");

  if (isDownloadableFile(uri)) {
    const extension = getFileExtension(uri);
    const fileName = getFileName(uri).replace(`.${extension}`, "");
    await downloadFile(uri, fileName);
  } else {
    await openFileInNewTab(uri);
  }
};

export const getPublicImageUrl = async (s3Key: string): Promise<string> => {
  if (isEmptyStorageKey(s3Key)) return "";

  if (isMikeSandbox() || isKamiliaSandbox()) {
    return getImageUrl(s3Key);
  }

  return `${CLOUD_FRONT_DOMAIN_NAME}/${s3Key}`;
};
